﻿class CustomProductForm extends HTMLElement {
  constructor() {
    super();
    // Core Elements
    this.form = this.querySelector('form');
    this.priceElement = document.querySelector(`#price-${this.dataset.sectionId}`);
    this.sizeTypeRadios = this.querySelectorAll('[name="properties[Size Type]"]');
    this.thicknessSelects = this.querySelectorAll('[data-thickness-select]');
    this.thicknessOptions = this.querySelectorAll('[data-thickness-select] option');
    this.shippingInfo = this.querySelector('[data-shipping-info]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');

    this.edgeFinishCheckboxes = this.querySelectorAll('[data-edge-finish-option]');

    // Standard Size Elements
    this.standardSizesContainer = this.querySelector('[data-standard-sizes]');
    this.standardVariantSelect = this.querySelector('[data-standard-variant-select]');
    this.standardVariantOptions = this.querySelectorAll('[data-standard-variant-select] option');
    this.standardOptionsContainer = this.querySelector('[data-standard-options-container]');
    this.standardDrillHolesCheckbox = this.querySelector('[data-standard-drill-holes]');
    this.standardDrillHolesNumberInput = this.querySelector('#standard-holes-number-' + this.dataset.sectionId);
    this.standardRoundedCornersCheckbox = this.querySelector('[data-standard-rounded-corners]');
    this.standardCornerOptions = this.querySelector('[data-standard-corner-options]');
    this.standardHoleOptions = this.querySelector('[data-standard-hole-options]');

    // Custom Size (Rectangle) Elements
    this.customSizesContainer = this.querySelector('[data-custom-sizes]');
    this.customVariantSelect = this.querySelector('[data-custom-variant-select]');
    this.customWidthInput = this.querySelector('[data-custom-width]');
    this.customLengthInput = this.querySelector('[data-custom-length]');
    this.drillHolesCheckbox = this.querySelector('[data-drill-holes]');
    this.drillHolesNumberInput = this.querySelector('#custom-holes-number-' + this.dataset.sectionId);
    this.customRoundedCornersCheckbox = this.querySelector('[data-custom-rounded-corners]');
    this.customCornerOptions = this.querySelector('[data-custom-corner-options]');
    this.customHoleOptions = this.querySelector('[data-custom-hole-options]');
    this.maxDimensionWarning = this.querySelector('#max-dimension-warning-' + this.dataset.sectionId);

    // Circle Elements
    this.circleSizesContainer = this.querySelector('[data-circle-sizes]');
    this.circleVariantSelect = this.querySelector('[data-circle-variant-select]');
    this.circleDiameterInput = this.querySelector('[data-circle-diameter]');
    this.centerHoleCheckbox = this.querySelector('[data-center-hole]');
    this.circleHoleOptions = this.querySelector('[data-circle-hole-options]');

    // State
    this.currentCustomVariant = null;
    this.currentCustomPricing = {};
    this.currentCircleVariant = null;
    this.currentCirclePricing = {};

    // Store all standard variant options for Safari compatibility
    this.allStandardVariantOptions = Array.from(this.standardVariantOptions).filter(opt => opt.value);

    // Clear standard variant options on initial load
    const placeholder = this.standardVariantSelect.querySelector('option[value=""]');
    this.standardVariantSelect.innerHTML = '';
    this.standardVariantSelect.appendChild(placeholder);

    // Debouncers
    this.debouncedCustomUpdate = this.debounce(this.updateCustomCalculation.bind(this), 300);
    this.debouncedCircleUpdate = this.debounce(this.updateCircleCalculation.bind(this), 300);
    this.debouncedStandardUpdate = this.debounce(this.updateStandardCalculation.bind(this), 300);

    // Properties for AJAX cart
    this.cartNotification = document.querySelector('cart-notification');
    this.body = document.body;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));


    this.bindEvents();
    this.onSizeTypeChange();
  }

  parseFraction(value) {
    if (!value) return 0;
    const str = value.toString().trim().replace('"', '');
    if (str.includes('/')) {
      const parts = str.split('/');
      return parseFloat(parts[0]) / parseFloat(parts[1]);
    }
    return parseFloat(str) || 0;
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  bindEvents() {
    this.sizeTypeRadios.forEach(radio => radio.addEventListener('change', this.onSizeTypeChange.bind(this)));
    this.thicknessSelects.forEach(select => select.addEventListener('change', this.onThicknessChange.bind(this)));

    this.edgeFinishCheckboxes.forEach(checkbox => checkbox.addEventListener('change', this.handleEdgeFinishChange.bind(this)));

    // Standard size events
    this.standardVariantSelect.addEventListener('change', this.onStandardVariantChange.bind(this));
    this.standardDrillHolesCheckbox.addEventListener('change', this.onStandardDrillHolesChange.bind(this));
    this.standardDrillHolesNumberInput.addEventListener('input', this.debouncedStandardUpdate);
    this.standardRoundedCornersCheckbox.addEventListener('change', this.updateStandardCalculation.bind(this));

    // Custom size events
    this.customWidthInput.addEventListener('input', this.debouncedCustomUpdate);
    this.customLengthInput.addEventListener('input', this.debouncedCustomUpdate);
    this.drillHolesCheckbox.addEventListener('change', this.onCustomDrillHolesChange.bind(this));
    this.drillHolesNumberInput.addEventListener('input', this.debouncedCustomUpdate);
    this.customRoundedCornersCheckbox.addEventListener('change', this.updateCustomCalculation.bind(this));

    // Circle events
    this.circleDiameterInput.addEventListener('input', this.debouncedCircleUpdate);
    this.centerHoleCheckbox.addEventListener('change', this.onCenterHoleChange.bind(this));
  }

  handleEdgeFinishChange(event) {
    const clickedCheckbox = event.currentTarget;
    const value = clickedCheckbox.dataset.edgeFinishOption;

    const container = clickedCheckbox.closest('[data-standard-sizes], [data-custom-sizes], [data-circle-sizes]');
    if (!container) return;

    const hiddenInput = container.querySelector('[data-edge-finish-value]');
    const otherCheckboxes = container.querySelectorAll(`[data-edge-finish-option]:not([data-edge-finish-option="${value}"])`);

    if (clickedCheckbox.checked) {
      // Uncheck other edge finish checkboxes in the same section
      otherCheckboxes.forEach(cb => cb.checked = false);
      // Set the hidden input's value and add the name attribute
      hiddenInput.value = value;
      hiddenInput.setAttribute('name', 'properties[Edge Finish]');
    } else {
      // If unchecked, clear the hidden input's value and remove the name attribute
      hiddenInput.value = '';
      hiddenInput.removeAttribute('name');
    }

    // Trigger the correct price update
    if (container.dataset.standardSizes !== undefined) {
      this.updateStandardCalculation();
    } else if (container.dataset.customSizes !== undefined) {
      this.updateCustomCalculation();
    } else if (container.dataset.circleSizes !== undefined) {
      this.updateCircleCalculation();
    }
  }

  getActiveThicknessValue() {
    const activeSelect = Array.from(this.thicknessSelects).find(select => !select.disabled);
    return activeSelect ? activeSelect.value : '';
  }

  updateThicknessOptions() {
    const selectedType = this.form.querySelector('[name="properties[Size Type]"]:checked').value;
    const allowedCircleThicknesses = ['1/8', '1/4'];

    this.thicknessOptions.forEach(option => {
      if (option.value === '') {
        option.hidden = false;
        return;
      }
      if (selectedType === 'Circle') {
        option.hidden = !allowedCircleThicknesses.includes(option.value);
      } else {
        option.hidden = false;
      }
    });
  }

  onSizeTypeChange() {
    const selectedType = this.form.querySelector('[name="properties[Size Type]"]:checked').value;

    if (selectedType === 'Circle') {
      const allowedCircleThicknesses = ['1/8', '1/4'];
      const currentThickness = this.getActiveThicknessValue();
      if (currentThickness && !allowedCircleThicknesses.includes(currentThickness)) {
        this.thicknessSelects.forEach(select => select.value = '');
      }
    }

    this.updateThicknessOptions();

    [this.standardSizesContainer, this.customSizesContainer, this.circleSizesContainer].forEach(container => {
      container.style.display = 'none';
      container.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true);
    });

    let activeContainer = null;
    if (selectedType === 'Standard Size') {
      activeContainer = this.standardSizesContainer;
    } else if (selectedType === 'Custom Size') {
      activeContainer = this.customSizesContainer;
    } else if (selectedType === 'Circle') {
      activeContainer = this.circleSizesContainer;
    }

    if (activeContainer) {
      activeContainer.style.display = 'block';
      activeContainer.querySelectorAll('input, select, textarea').forEach(el => el.disabled = false);

      if (selectedType === 'Standard Size') {
        this.onStandardDrillHolesChange();
        this.updateStandardCalculation();
      } else if (selectedType === 'Custom Size') {
        this.onCustomDrillHolesChange();
        this.updateCustomCalculation();
      } else if (selectedType === 'Circle') {
        this.onCenterHoleChange();
      }
    }

    this.onStandardVariantChange();
    if (selectedType === 'Custom Size') {
      this.updateCustomCalculation();
    } else if (selectedType === 'Circle') {
      this.updateCircleCalculation();
    }

    this.updateAddToCartState();
  }

  onThicknessChange(event) {
    const selectedThickness = event.target.value;
    this.thicknessSelects.forEach(select => {
      if (select !== event.target) {
        select.value = selectedThickness;
      }
    });

    this.standardVariantSelect.value = '';

    // Remove all options except the placeholder
    const placeholder = this.standardVariantSelect.querySelector('option[value=""]');
    this.standardVariantSelect.innerHTML = '';
    this.standardVariantSelect.appendChild(placeholder);

    // Add back only the matching options
    if (selectedThickness) {
      this.allStandardVariantOptions.forEach(option => {
        if (option.dataset.thickness === selectedThickness) {
          this.standardVariantSelect.appendChild(option.cloneNode(true));
        }
      });
    }

    this.standardOptionsContainer.style.display = 'none';

    const customOptions = this.customVariantSelect.querySelectorAll('option');
    let customVariantFound = false;
    customOptions.forEach(option => {
      if (option.dataset.thickness === selectedThickness) {
        option.selected = true;
        this.currentCustomVariant = option;
        this.currentCustomPricing = {
          sfPrice: parseFloat(option.dataset.sfPrice),
          minPrice: parseFloat(option.dataset.minPrice),
          maxWidth: parseInt(option.dataset.maxWidth),
          maxLength: parseInt(option.dataset.maxLength),
          edgePolishPricePerFoot: parseFloat(option.dataset.edgePolishPricePerFoot),
          drillHolePrice: parseFloat(option.dataset.drillHolePrice)
        };
        customVariantFound = true;
      }
    });
    if (!customVariantFound) this.currentCustomVariant = null;

    const circleOptions = this.circleVariantSelect.querySelectorAll('option');
    let circleVariantFound = false;
    circleOptions.forEach(option => {
      if (option.dataset.thickness === selectedThickness) {
        option.selected = true;
        this.currentCircleVariant = option;
        this.currentCirclePricing = {
          sfPrice: parseFloat(option.dataset.sfPrice) || 0,
          minPrice: parseFloat(option.dataset.minPrice) || 0,
          edgePolishPrice: parseFloat(option.dataset.edgePolishPrice) || 1.50
        };
        circleVariantFound = true;
      }
    });
    if (!circleVariantFound) this.currentCircleVariant = null;

    const selectedType = this.form.querySelector('[name="properties[Size Type]"]:checked').value;
    if (selectedType === 'Custom Size') {
      this.updateCustomCalculation();
    } else if (selectedType === 'Circle') {
      this.updateCircleCalculation();
    }
    this.updateAddToCartState();
  }

  updateMainPrice(price) {
    if (!this.priceElement) return;

    const priceItem = this.priceElement.querySelector('.amount');
    const simplePriceHTML = `<span class="price-item price-item--regular">$${price.toFixed(2)}</span>`;

    if (priceItem) {
      priceItem.innerHTML = `$${price.toFixed(2)}`;
    } else {
      this.priceElement.innerHTML = simplePriceHTML;
    }
  }

  onStandardVariantChange() {
    const selectedOption = this.standardVariantSelect.options[this.standardVariantSelect.selectedIndex];
    const shouldShowOptions = selectedOption && selectedOption.value;

    this.standardOptionsContainer.style.display = shouldShowOptions ? 'block' : 'none';

    if (shouldShowOptions) {
      this.standardDrillHolesCheckbox.disabled = false;
      this.standardRoundedCornersCheckbox.disabled = false;
      this.edgeFinishCheckboxes.forEach(checkbox => checkbox.disabled = false);

      const edgeFinishHiddenInput = this.standardOptionsContainer.querySelector('[data-edge-finish-value]');
      edgeFinishHiddenInput.disabled = false;

      this.updateStandardCalculation();
    } else {
      this.standardOptionsContainer.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true);
    }

    this.updateAddToCartState();
  }

  updateStandardCalculation() {
    const areCornersRounded = this.standardRoundedCornersCheckbox.checked;
    this.standardCornerOptions.style.display = areCornersRounded ? 'block' : 'none';
    this.standardCornerOptions.querySelectorAll('select').forEach(el => el.disabled = !areCornersRounded);

    const selectedOption = this.standardVariantSelect.options[this.standardVariantSelect.selectedIndex];
    if (!selectedOption || !selectedOption.value || !this.currentCustomPricing.edgePolishPricePerFoot) {
      return;
    }

    const basePrice = parseFloat(selectedOption.dataset.basePrice) || 999.99;
    const dimensions = selectedOption.dataset.size.split('x');
    const width = parseFloat(dimensions[0]) || 0;
    const length = parseFloat(dimensions[1]) || 0;

    const selectedEdgeFinish = this.standardSizesContainer.querySelector('[data-edge-finish-value]').value;
    const isDrillHolesChecked = this.standardDrillHolesCheckbox.checked;
    const drillHoles = isDrillHolesChecked ? (parseInt(this.standardDrillHolesNumberInput.value) || 1) : 0;

    let optionsTotal = 0;
    if ((selectedEdgeFinish === 'Edge Polish' || selectedEdgeFinish === 'Edge Sand') && width > 0 && length > 0) {
      const perimeter = (length * 2) + (width * 2);
      const linearFeet = perimeter / 12;
      const edgeCost = linearFeet * this.currentCustomPricing.edgePolishPricePerFoot;
      optionsTotal += edgeCost;
    }

    const drillCost = drillHoles * this.currentCustomPricing.drillHolePrice;
    optionsTotal += drillCost;

    const cornerCost = areCornersRounded ? 8.00 : 0;
    optionsTotal += cornerCost;

    const totalPrice = basePrice + optionsTotal;

    this.updateMainPrice(totalPrice);
  }

  updateCustomCalculation() {
    const areCornersRounded = this.customRoundedCornersCheckbox.checked;
    this.customCornerOptions.style.display = areCornersRounded ? 'block' : 'none';
    this.customCornerOptions.querySelectorAll('select').forEach(el => el.disabled = !areCornersRounded);

    if (!this.currentCustomVariant) {
      return;
    }

    const width = parseFloat(this.customWidthInput.value) || 0;
    const length = parseFloat(this.customLengthInput.value) || 0;

    const selectedEdgeFinish = this.customSizesContainer.querySelector('[data-edge-finish-value]').value;

    const isDrillHolesChecked = this.drillHolesCheckbox.checked;
    const drillHoles = isDrillHolesChecked ? (parseInt(this.drillHolesNumberInput.value) || 1) : 0;

    this.validateDimensions(width, length);

    if (width > 0 && length > 0) {
      const totalInches = width * length;
      const squareFeet = totalInches / 144;
      const basePrice = squareFeet * this.currentCustomPricing.sfPrice;
      const finalBasePrice = Math.max(basePrice, this.currentCustomPricing.minPrice);

      let optionsTotal = 0;
      if (selectedEdgeFinish === 'Edge Polish' || selectedEdgeFinish === 'Edge Sand') {
        const perimeter = (length * 2) + (width * 2);
        const linearFeet = perimeter / 12;
        const edgeCost = linearFeet * this.currentCustomPricing.edgePolishPricePerFoot;
        optionsTotal += edgeCost;
      }

      const drillCost = drillHoles * this.currentCustomPricing.drillHolePrice;
      optionsTotal += drillCost;

      const cornerCost = areCornersRounded ? 8.00 : 0;
      optionsTotal += cornerCost;

      const totalPrice = finalBasePrice + optionsTotal;

      this.updateMainPrice(totalPrice);
    }

    this.updateAddToCartState();
  }

  validateDimensions(width, length) {
    this.maxDimensionWarning.style.display = 'none';
    this.shippingInfo.style.display = 'none';
    if (width > 0 && length > 0) {
      if ((width > this.currentCustomPricing.maxWidth && length > this.currentCustomPricing.maxLength) ||
          (width > this.currentCustomPricing.maxLength && length > this.currentCustomPricing.maxWidth)) {
        this.maxDimensionWarning.textContent = `Maximum dimensions for this thickness: ${this.currentCustomPricing.maxWidth}" x ${this.currentCustomPricing.maxLength}"`;
        this.maxDimensionWarning.style.display = 'block';
      }
      if (Math.min(width, length) > 36 || Math.max(width, length) > 48) {
        this.shippingInfo.style.display = 'block';
      }
    }
  }

  onStandardDrillHolesChange() {
    const showHoleOptions = this.standardDrillHolesCheckbox.checked;
    this.standardHoleOptions.style.display = showHoleOptions ? 'grid' : 'none';
    this.standardHoleOptions.querySelectorAll('input, select, textarea').forEach(el => el.disabled = !showHoleOptions);
    this.updateStandardCalculation();
  }

  onCustomDrillHolesChange() {
    const showHoleOptions = this.drillHolesCheckbox.checked;
    this.customHoleOptions.style.display = showHoleOptions ? 'grid' : 'none';
    this.customHoleOptions.querySelectorAll('input, select, textarea').forEach(el => el.disabled = !showHoleOptions);
    this.updateCustomCalculation();
  }

  onCenterHoleChange() {
    const showHoleOptions = this.centerHoleCheckbox.checked;
    this.circleHoleOptions.style.display = showHoleOptions ? 'block' : 'none';
    this.circleHoleOptions.querySelectorAll('select').forEach(el => el.disabled = !showHoleOptions);
    this.updateCircleCalculation();
  }

  updateCircleCalculation() {
    if (!this.currentCircleVariant || !this.getActiveThicknessValue()) {
      return;
    }

    const diameter = parseFloat(this.circleDiameterInput.value) || 0;

    const selectedEdgeFinish = this.circleSizesContainer.querySelector('[data-edge-finish-value]').value;

    if (diameter > 0) {
      const squareFeet = (diameter * diameter) / 144;
      const basePrice = (squareFeet * this.currentCirclePricing.sfPrice) * 2;
      const finalBasePrice = Math.max(basePrice, this.currentCirclePricing.minPrice);

      let optionsTotal = 0;
      if (selectedEdgeFinish === 'Edge Polish' || selectedEdgeFinish === 'Edge Sand') {
        const circumference = diameter * 4;
        const linearFeet = circumference / 12;
        const edgeCost = linearFeet * this.currentCirclePricing.edgePolishPrice;
        optionsTotal += edgeCost;
      }

      const totalPrice = finalBasePrice + optionsTotal;

      this.updateMainPrice(totalPrice);
    }

    this.updateAddToCartState();
  }

  updateAddToCartState() {
    const selectedType = this.form.querySelector('[name="properties[Size Type]"]:checked').value;
    const thicknessSelected = this.getActiveThicknessValue() !== '';
    let canAddToCart = false;
    this.shippingInfo.style.display = 'none';

    if (selectedType === 'Standard Size') {
      const standardVariantSelected = this.standardVariantSelect.value !== '';
      canAddToCart = thicknessSelected && standardVariantSelected;
      const selectedOption = this.standardVariantSelect.options[this.standardVariantSelect.selectedIndex];
      if (selectedOption && selectedOption.value) {
        this.shippingInfo.style.display = selectedOption.dataset.upsShippable === 'true' ? 'none' : 'block';
      }
    } else if (selectedType === 'Custom Size') {
      const width = parseFloat(this.customWidthInput.value) || 0;
      const length = parseFloat(this.customLengthInput.value) || 0;
      const validDimensions = this.currentCustomPricing && width > 0 && length > 0 &&
        ((width <= this.currentCustomPricing.maxWidth && length <= this.currentCustomPricing.maxLength) ||
         (width <= this.currentCustomPricing.maxLength && length <= this.currentCustomPricing.maxWidth));
      canAddToCart = thicknessSelected && validDimensions;
      if (canAddToCart) {
        this.shippingInfo.style.display = (Math.min(width, length) > 36 || Math.max(width, length) > 48) ? 'block' : 'none';
      }
    } else if (selectedType === 'Circle') {
      const diameter = parseFloat(this.circleDiameterInput.value) || 0;
      canAddToCart = thicknessSelected && diameter > 0;
      if (canAddToCart) {
        this.shippingInfo.style.display = diameter > 36 ? 'block' : 'none';
      }
    }

    this.addToCartButton.disabled = !canAddToCart;
    this.addToCartButton.hidden = !canAddToCart;
    this.addToCartButton.style.display = canAddToCart ? 'inline-flex' : 'none';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();

    if (this.addToCartButton.classList.contains('loading')) return;

    this.addToCartButton.setAttribute('aria-disabled', true);
    this.addToCartButton.classList.add('loading');
    this.handleErrorMessage();

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      }
    };

    let formData = new FormData(this.form);

    // Calculate and add weight property based on size type
    const selectedType = this.form.querySelector('[name="properties[Size Type]"]:checked').value;
    const thickness = this.parseFraction(this.getActiveThicknessValue());
    let weight = 0;

    if (selectedType === 'Standard Size') {
      const selectedOption = this.standardVariantSelect.options[this.standardVariantSelect.selectedIndex];
      if (selectedOption && selectedOption.value) {
        const dimensions = selectedOption.dataset.size.split('x');
        const length = parseFloat(dimensions[0]) || 0;
        const width = parseFloat(dimensions[1]) || 0;
        weight = length * width * thickness * 0.043;
      }
    } else if (selectedType === 'Custom Size') {
      const length = parseFloat(this.customLengthInput.value) || 0;
      const width = parseFloat(this.customWidthInput.value) || 0;
      weight = length * width * thickness * 0.043;
    } else if (selectedType === 'Circle') {
      const diameter = parseFloat(this.circleDiameterInput.value) || 0;
      weight = diameter * diameter * thickness * 0.043;
    }

    formData.append('properties[Weight]', weight.toFixed(3));

    formData.append('sections', this.getSectionsToRender().map((section) => section.section));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('aria-disabled');
      });
  }

  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) {
        return;
      }
      const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    }));

    this.body.classList.add('open-cc');
    document.getElementById('Cart-Drawer').classList.add('active');
    dispatchCustomEvent('cart-drawer:open');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

customElements.define('custom-product-form', CustomProductForm);
